declare global {
  interface Window {
    ani_collapsiblesInitialized?: boolean;
  }
}

export type ToggleCollapsibleOptions = {
  /** The original event that toggled the collapsible.
   *  If set it will be passed on to the generated ani:toggle event.
   */
  event?: Event;

  /** Used to force the collapsible state - true for open, false for close. */
  forceState?: boolean;
};

export type ToggleCollapsibleEvent = CustomEvent<{
  /** true if the collapsible is opening, false otherwise. */
  isOpening: boolean;

  /** The original event, if any, that toggled the collapsible state. */
  originalEvent?: Event;
}>;

const initializeGlobalListeners = () => {
  if (!window.ani_collapsiblesInitialized) {
    window.ani_collapsiblesInitialized = true;

    // Close on press escape
    document.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        document.querySelectorAll<HTMLElement>("[data-collapsible]").forEach(element => {
          const autoInit = element.dataset.collapsibleAutoInit === "true";
          const closeOnEscape = element.dataset.collapsibleCloseOnEscape === "true";

          if (autoInit && closeOnEscape && collapsibleIsOpen(element)) {
            toggleCollapsible(element, { event });
          }
        });
      }
    });

    // Close on click outside
    document.addEventListener("click", event => {
      document.querySelectorAll<HTMLElement>("[data-collapsible]").forEach(element => {
        const autoInit = element.dataset.collapsibleAutoInit === "true";

        const closeOnClickOutside =
          element.dataset.collapsibleCloseOnClickOutside === "true";

        if (autoInit && closeOnClickOutside && collapsibleIsOpen(element)) {
          const triggers = [
            ...document.querySelectorAll<HTMLElement>(`[aria-controls='${element.id}']`)
          ];

          const isOutsideCollapsibleElement =
            element.contains(event.target as Node) === false;

          const isOutsideTriggerElements = triggers.every(
            trigger => trigger.contains(event.target as Node) === false
          );

          if (isOutsideCollapsibleElement && isOutsideTriggerElements) {
            toggleCollapsible(element, {
              event
            });
          }
        }
      });
    });
  }
};

/**
 * Checks if a collapsible is open or closed.
 * @param element The collapsible element.
 * @returns true if the collapsible is open, false otherwise.
 */
export const collapsibleIsOpen = (element: HTMLElement) => element.ariaHidden !== "true";

/**
 * Expands, or collapses, a collapsible element.
 * @param element The collapsible element.
 * @param options Optional options object.
 */
export const toggleCollapsible = (
  element: HTMLElement,
  options?: ToggleCollapsibleOptions
) => {
  if (element.dataset.isAnimating === undefined) {
    const { event, forceState } = options ?? {};

    const triggers = document.querySelectorAll<HTMLElement>(
      `[aria-controls='${element.id}']`
    );

    const innerContainer = element.querySelector(
      "[data-collapsible-inner]"
    ) as HTMLElement;

    if (!innerContainer) {
      throw `Failed to toggle collapsible with ID ${element.id}: inner container not found`;
    }

    // "long" or "short"
    const transitionSpeed = element.dataset.collapsibleTransition;

    const durationIn = transitionSpeed === "short" ? 250 : 500;
    const durationOut = transitionSpeed === "short" ? 100 : 250;

    const shouldExpand = forceState ?? !collapsibleIsOpen(element);

    element.style.height = `${innerContainer.getBoundingClientRect().height}px`;

    requestAnimationFrame(() => {
      triggers.forEach(trigger =>
        trigger.setAttribute("aria-expanded", String(shouldExpand))
      );
      element.setAttribute("aria-hidden", String(!shouldExpand));
      element.setAttribute("data-is-animating", "");
      innerContainer.inert = !shouldExpand;

      if (shouldExpand) {
        setTimeout(() => {
          element.style.height = "auto";
          element.style.overflow = "visible";
          element.removeAttribute("data-is-animating");
        }, durationIn);
      } else {
        element.style.height = "0";
        element.style.overflow = "hidden";

        setTimeout(() => {
          element.removeAttribute("data-is-animating");
        }, durationOut);
      }
    });

    const collapsibleEvent: ToggleCollapsibleEvent = new CustomEvent("ani:toggle", {
      detail: {
        isOpening: shouldExpand,
        originalEvent: event
      }
    });

    if (shouldExpand !== collapsibleIsOpen(element)) {
      element.dispatchEvent(collapsibleEvent);
    }
  }
};

/**
 * Initializes a collapsible component.
 * @param element The collapsible element.
 */
export const initializeCollapsible = (element: HTMLElement) => {
  const triggers = document.querySelectorAll<HTMLElement>(
    `[aria-controls='${element.id}']`
  );

  triggers.forEach(trigger =>
    trigger.addEventListener("click", event => {
      toggleCollapsible(element, {
        event
      });
    })
  );
};

/** Finds all auto-initializable collapsibles, and initializes them. */
export const initializeAllCollapsibles = () => {
  initializeGlobalListeners();

  document.querySelectorAll<HTMLElement>("[data-collapsible]").forEach(element => {
    if (element.dataset.collapsibleAutoInit !== "false") {
      initializeCollapsible(element);
    }
  });
};
